import { FormikProvider, useFormik } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';

import BackButton from '@perpay-web/components/base/BackButton/BackButton';
import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import Modal from '@perpay-web/components/base/Modal/Modal';
import NonFieldErrors from '@perpay-web/components/base/NonFieldErrors/NonFieldErrors';
import FormikTextInput from '@perpay-web/components/base/TextInput/FormikTextInput';
import FocusContent from '@perpay-web/components/composite/FocusLayout/FocusContent';
import FocusFooter from '@perpay-web/components/composite/FocusLayout/FocusFooter';
import FocusHeader from '@perpay-web/components/composite/FocusLayout/FocusHeader';
import FocusLayout from '@perpay-web/components/composite/FocusLayout/FocusLayout';
import Stack from '@perpay-web/components/design-system/Stack/Stack';
import DefaultHeader from '@perpay-web/fintech/components/base/Header/DefaultHeader';
import { useFullHeader } from '@perpay-web/fintech/hooks/useFullHeaderContext';
import { useProfileMFAContext } from '@perpay-web/fintech/hooks/useProfileMFAContext';
import { paths } from '@perpay-web/fintech/props/appPaths';
import { mfaFormSchema } from '@perpay-web/fintech/validators/formBased';
import { useIsMobile } from '@perpay-web/hooks/useIsMobile';
import { EndToEndTestIdProvider } from '@perpay-web/utils/endToEndTestIds';
import {
    extractTimeFromString,
    formatMaskedEmailWithBullets,
    formatMaskedPhoneNumberWithBullets,
    makePlural,
} from '@perpay-web/utils/stringUtils';
import { validateForm } from '@perpay-web/validators/formBased';

import styles from './MFAForm.scss';

const defaultLeftButton = <BackButton href={paths.login.path} />;

const MFAForm = ({
    error = {
        send: null,
        attempt: null,
        resend: null,
        throttle: null,
    },
    isLoading = false,
    onSubmit,
    onSubmitResend,
    userInfo = null,
    data = {
        otpUuid: '',
        validUntil: '',
        contactInfo: {
            email: '',
            phone: '',
        },
    },
    initialValues = {
        code: '',
    },

    headerCurrentStep = null,
    hideDefaultHeaderOnMobile = false,
    headerSteps = null,
    headerLeftButtonElement = defaultLeftButton,
    headerRightButtonElement = null,
    goBackPath = paths.login.path,
    onSuccess,
}) => {
    const navigate = useNavigate();

    const { validationSuccess, setValidationSuccess } = useProfileMFAContext();

    useEffect(() => {
        if (validationSuccess && onSuccess) {
            onSuccess();
            setValidationSuccess(false);
        }
    }, [validationSuccess, onSuccess, setValidationSuccess]);

    const { isMobile } = useIsMobile();
    const headerElement = useMemo(
        () => (
            <FocusHeader
                steps={headerSteps}
                currentStep={headerCurrentStep}
                leftButtonElement={headerLeftButtonElement}
                rightButtonElement={headerRightButtonElement}
            />
        ),
        [
            headerSteps,
            headerCurrentStep,
            headerLeftButtonElement,
            headerRightButtonElement,
        ],
    );

    const renderHeaderCallback = useCallback(() => {
        if (hideDefaultHeaderOnMobile) {
            return isMobile ? null : <DefaultHeader />;
        }
        return isMobile ? headerElement : <DefaultHeader />;
    }, [isMobile, headerElement, hideDefaultHeaderOnMobile]);
    useFullHeader(renderHeaderCallback);

    const isPhoneProvided = !!data.contactInfo.phone;

    const goBack = () => {
        navigate(goBackPath);
    };

    const formik = useFormik({
        initialValues,
        validateOnMount: true,
        onSubmit: (values, formikHelpers) => {
            // Reset touched to disable form submission if user entered invalid code
            // previously and hasn't updated their input.
            formikHelpers.setTouched({});
            onSubmit(values.code);
        },
        validate: (values) => validateForm(values, mfaFormSchema),
    });

    const contactDisclaimer = userInfo
        ? `Before you can update your personal information, we need to verify 
        that you are who you say you are. Please enter the 6-digit authentication code 
        we sent to ${formatMaskedPhoneNumberWithBullets(userInfo.mobilePhone)} and 
        ${formatMaskedEmailWithBullets(userInfo.email)}.`
        : `Please enter the 6 digit authentication code we sent to 
        ${isPhoneProvided ? `${data.contactInfo.phone} and ` : ''}
        ${data.contactInfo.email}.`;

    return (
        <FormikProvider value={formik}>
            {error && (error.send || error.throttle) ? (
                <Modal
                    showClose={false}
                    containerClassName={styles.modal__container}
                >
                    <div className={styles.modal__content}>
                        <div className={styles.modal__header}>
                            {error.send
                                ? 'Failed to send MFA code.'
                                : 'You have reached maximum attempts.'}
                        </div>
                        <div className={styles.modal__body}>
                            {error.send
                                ? 'Please contact customer service for support or try again.'
                                : `Please contact customer service for support or try again in ${extractTimeFromString(error.throttle.message[0])}.`}
                        </div>
                        <CustomButton action='primary' onClick={goBack}>
                            OK
                        </CustomButton>
                    </div>
                </Modal>
            ) : null}
            <form onSubmit={formik.handleSubmit}>
                <FocusLayout className={styles['focus-layout']}>
                    {!isMobile || hideDefaultHeaderOnMobile
                        ? headerElement
                        : null}
                    <FocusContent>
                        <Stack className={styles['inner-stack']}>
                            <p className={styles['responsive-small-header']}>
                                Enter authentication code
                            </p>
                            <p className={styles.contact}>
                                {contactDisclaimer}
                            </p>
                        </Stack>
                        <EndToEndTestIdProvider testId='confirmation-code'>
                            <FormikTextInput
                                autoComplete='one-time-code'
                                label='Authentication Code'
                                placeholder='XXXXXX'
                                name='code'
                                type='tel'
                                maxLength='6'
                            />
                        </EndToEndTestIdProvider>
                        {!error.resend && (
                            <div className={styles.resend}>
                                {data.resendsLeft >= 0 ? (
                                    <>
                                        You have{' '}
                                        {data.resendsLeft > 0
                                            ? data.resendsLeft
                                            : 'no'}{' '}
                                        {makePlural(
                                            data.resendsLeft,
                                            'resend',
                                            'resends',
                                        )}{' '}
                                        left.
                                    </>
                                ) : (
                                    <>Didn&apos;t receive a code?</>
                                )}
                                &nbsp;
                                {data.resendsLeft !== 0 && (
                                    <CustomButton
                                        action='inline-link'
                                        onClick={() => onSubmitResend()}
                                    >
                                        Resend
                                    </CustomButton>
                                )}
                            </div>
                        )}

                        {error && error.attempt ? (
                            <NonFieldErrors errors={error.attempt} />
                        ) : (
                            error &&
                            error.resend && (
                                <NonFieldErrors errors={error.resend} />
                            )
                        )}
                    </FocusContent>
                    <FocusFooter>
                        <EndToEndTestIdProvider testId='mfa-form'>
                            <CustomButton
                                action='primary'
                                className={styles.verify__button}
                                disabled={!formik.isValid}
                                loading={isLoading}
                                type='submit'
                            >
                                Verify
                            </CustomButton>
                        </EndToEndTestIdProvider>
                    </FocusFooter>
                </FocusLayout>
            </form>
        </FormikProvider>
    );
};

export default MFAForm;
